/**
 * EditorHeader -- 에디터 상단 헤더
 *
 * 뒤로가기, 제목, 저장 상태 표시등, [저장] [발행] 버튼
 */

import { useNavigate } from 'react-router'
import { ArrowLeft, Eye, Save, Send } from 'lucide-react'
import { cn } from '../../lib/cn.js'
import { Button } from '../ui/index.js'
import type { FlatInvitation } from '../../stores/invitationStore.js'
import type { SaveStatus } from './useAutoSave.js'
import styles from './EditorHeader.module.css'

interface EditorHeaderProps {
  invitation: FlatInvitation
  saveStatus: SaveStatus
  onSave: () => void
  onPublish: () => void
  onPreview: () => void
  isDirty: boolean
}

const STATUS_LABEL: Record<SaveStatus, string> = {
  saved: '저장됨',
  saving: '저장 중...',
  dirty: '변경사항 있음',
  error: '저장 실패',
}

export function EditorHeader({ invitation, saveStatus, onSave, onPublish, onPreview, isDirty }: EditorHeaderProps) {
  const navigate = useNavigate()
  const isPublished = invitation.status === 'PUBLISHED'

  const displayName =
    invitation.groom?.name && invitation.bride?.name
      ? `${invitation.groom.name} & ${invitation.bride.name}`
      : '청첩장 편집'

  return (
    <header className={styles.root}>
      <div className={styles.left}>
        <button
          type="button"
          className={styles.back}
          onClick={() => navigate('/studio/invitations')}
          aria-label="뒤로가기"
        >
          <ArrowLeft size={20} />
        </button>
        <div className={styles.info}>
          <h1 className={styles.title}>{displayName}</h1>
          <span className={cn(styles.status, styles[saveStatus])}>
            <span className={styles.statusDot} />
            {STATUS_LABEL[saveStatus]}
          </span>
        </div>
      </div>

      <div className={styles.actions}>
        <Button
          variant="ghost"
          size="sm"
          onClick={onPreview}
        >
          <Eye size={14} />
          미리보기
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onSave}
          disabled={!isDirty || saveStatus === 'saving'}
        >
          <Save size={14} />
          저장
        </Button>
        <Button size="sm" disabled={isPublished || saveStatus === 'saving'} onClick={onPublish}>
          <Send size={14} />
          {isPublished ? '발행됨' : '발행'}
        </Button>
      </div>
    </header>
  )
}
